import { AutocompleteItem } from "@/types/autocomplete";
import React from "react";
import { createPortal } from "react-dom";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
} from "@floating-ui/react";

interface AutocompleteProps {
  items: AutocompleteItem[];
  query: string;
  isLoading: boolean;
  onSelect: (item: any) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  items,
  query,
  isLoading,
  onSelect,
  onClose,
  anchorRef,
}) => {
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    elements: {
      reference: anchorRef.current,
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 10 }),
      shift({ padding: 10 }),
      size({
        apply({ availableHeight, rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(availableHeight - 10, 400)}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const renderSuggestionNames = (name: string, query: string) => {
    const result: React.ReactNode[] = [];
    let i = 0;
    while (i < name.length) {
      const substring = name.substring(i, i + query.length);
      if (substring.toLowerCase() === query.toLowerCase()) {
        result.push(<strong key={i}>{substring}</strong>);
        i += query.length;
      } else {
        result.push(name[i]);
        i += 1;
      }
    }
    return result;
  };

  // No mostrar nada si el input está vacío
  if (!query || query.trim() === "") {
    return null;
  }

  const dropdownContent = (
    <div
      ref={refs.setFloating}
      className="Autocomplete bg-background shadow-lg rounded-lg z-[9999] border border-border overflow-y-auto autocomplete-scrollbar"
      style={floatingStyles}
    >
      <style>{`
        .autocomplete-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .autocomplete-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .autocomplete-scrollbar::-webkit-scrollbar-thumb {
          background-color: hsl(var(--muted-foreground) / 0.3);
          border-radius: 3px;
        }
        .autocomplete-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: hsl(var(--muted-foreground) / 0.5);
        }
        .autocomplete-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: hsl(var(--muted-foreground) / 0.3) transparent;
        }
      `}</style>
      {isLoading ? (
        <div className="w-full p-4 flex items-center justify-center">
          <div className="flight-loader"></div>
          <span className="ml-2 text-muted-foreground text-sm">Cargando...</span>
        </div>
      ) : items && items.length > 0 ? (
        <ul className="Autocomplete__list py-1" role="listbox">
          {items.map((item, i) => (
            <li
              key={i}
              role="option"
              className="cursor-pointer p-3 flex items-center gap-2 transition-colors hover:bg-primary/5"
              onClick={() => onSelect(item.data)}
            >
              {item.icon}
              <span className="text-foreground">{renderSuggestionNames(item.displayValue, query)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div 
          className="w-full p-4 text-center"
          role="status"
          aria-live="polite"
        >
          <p className="text-muted-foreground text-sm">No hay resultados para su búsqueda</p>
        </div>
      )}
    </div>
  );

  return createPortal(dropdownContent, document.body);
};

export default Autocomplete;